import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface BatteryCoachProps {
  vin: string;
  puid: string;
}

const BATTERY_SCORE_MASTER = 95;
const BATTERY_SCORE_GUARDIAN = 90;

enum BatteryCoachLevel {
  MASTER,
  GUARDIAN,
  SAVER,
}

function getBatteryCoachLevel(score: number): BatteryCoachLevel {
  if (score >= BATTERY_SCORE_MASTER) {
    return BatteryCoachLevel.MASTER;
  }
  if (score >= BATTERY_SCORE_GUARDIAN) {
    return BatteryCoachLevel.GUARDIAN;
  }
  return BatteryCoachLevel.SAVER;
}

function BatteryCoach(props: BatteryCoachProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, puid}: BatteryCoachProps) {
  const {t} = useInferableTranslation();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryManagementController.getEvBatteryManagementScore), vin, puid],
    queryFn: () => diApi.evBatteryManagementController.getEvBatteryManagementScore({vin, puid}),
  });

  const {score} = data.data;
  const level = getBatteryCoachLevel(score);
  return (
    <>
      <div className="section_container_header level_header">
        <h4 className="title_xxsmall">{t('T30559')}</h4>
        <div className="tooltip_area tooltip_area_single">
          <button
            type="button"
            className="btn_tooltip"
            aria-controls="tooltipStatement01"
            aria-expanded="false"
            aria-describedby="tooltipBox05"
            onClick={() => ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_COACH.getFullPath())}
          >
            <span
              className="ic ic_information_16px"
              aria-hidden="true"
            ></span>
            <span className="blind">{t('T10044')}</span>
          </button>
        </div>
      </div>
      <BatteryLevelInfoBox level={level} />
    </>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_container_header level_header">
        <h4 className="title_xxsmall">{t('T30559')}</h4>
        <div className="tooltip_area tooltip_area_single">
          <button
            type="button"
            className="btn_tooltip"
            aria-controls="tooltipStatement01"
            aria-expanded="false"
            aria-describedby="tooltipBox05"
            onClick={() => ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_COACH.getFullPath())}
          >
            <span
              className="ic ic_information_16px"
              aria-hidden="true"
            ></span>
            <span className="blind">{t('T10044')}</span>
          </button>
        </div>
      </div>
      <div className="section_container_box level_info_box skeleton">
        <div className="level_info">
          <span className="level_text">&nbsp;</span>
          <p className="status_desc">&nbsp;</p>
        </div>
      </div>
    </>
  );
}

interface BatteryLevelInfoBoxProps {
  level: BatteryCoachLevel;
}

function BatteryLevelInfoBox({level}: BatteryLevelInfoBoxProps) {
  const {t} = useInferableTranslation();
  switch (level) {
    case BatteryCoachLevel.MASTER:
      return (
        <div className="section_container_box level_info_box">
          <div className="level_info">
            <span className="level_text">{t('T30560')}</span>
            <p className="status_desc">{t('T30561')}</p>
          </div>
          <span className="ic ic_master"></span>
        </div>
      );
    case BatteryCoachLevel.GUARDIAN:
      return (
        <div className="section_container_box level_info_box">
          <div className="level_info">
            <span className="level_text">{t('T30562')}</span>
            <p className="status_desc">{t('T30563')}</p>
          </div>
          <span className="ic ic_guardian"></span>
        </div>
      );
    case BatteryCoachLevel.SAVER:
    default:
      return (
        <div className="section_container_box level_info_box">
          <div className="level_info">
            <span className="level_text">{t('T30564')}</span>
            <p className="status_desc">{t('T30565')}</p>
          </div>
          <span className="ic ic_saver"></span>
        </div>
      );
  }
}

export default BatteryCoach;
